import { FC } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  FormHelperText,
  Typography,
  FormControl,
  OutlinedInput
} from '@mui/material';
import TermsOfUseText from './TermsOfUseText';
import OdForm, { OnSubmit } from '../formik/OdForm';
import TextField from '../formik/TextField';
import { useSimpleSaveAction } from '@/controller/SaveContextProvider';
import { useDispatch } from 'react-redux';
import { raiseUserInvitationSucceed } from '@/actions/invitation';
import { rPassword } from '@/pages/authentication/LoginContext';


export interface InvitationConfirmFormProps {
  email: string;
  code: string;
  cid: string;
  isSsoEnabled: boolean;
}

const emptySchema = Yup.object().shape({})
const validationSchema = 
  Yup.object().shape({
    password: Yup.string().min(12).max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .required('Password is required'),
    confirmPassword: Yup.string().min(12).max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm Password is required'),
  })

const InvitationConfirmForm: FC<InvitationConfirmFormProps> = ({
  email,
  code,
  cid,
  isSsoEnabled,
}) => {
  const save = useSimpleSaveAction()
  const dispatch = useDispatch()
  const initialValues = {
    password: '',
    confirmPassword: '',
    submit: null
  }
  
  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setSubmitting,
    setErrors,
    setStatus,
  }) => {
    save({ code, cid, ...values }, {
      onSuccess: () => {
        setSubmitting(false)
        setStatus({ success: true })
        dispatch(raiseUserInvitationSucceed(email, values.password, code, isSsoEnabled))
      },
      onFailure: (err:any) => {
        setSubmitting(false)
        setErrors({ submit: err })
        setStatus({ success: false })
      },
    })

  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema: !isSsoEnabled ? validationSchema : emptySchema })
  const { errors, isSubmitting } = formikCtrl

  return (
    <Box>
      <Typography color="textPrimary" variant="h6" sx={{ pb: 1 }} >
        Email Address
      </Typography>
      <FormControl variant="outlined" fullWidth sx={{ pb: 3 }} >
        <OutlinedInput 
          value={email}
          disabled
          inputProps={{ 'aria-label': 'email address' }}
        />
      </FormControl>
      <OdForm formikCtrl={formikCtrl}>
        <Typography color="textPrimary" variant="h6" sx={{ pb: 0 }} >
          {isSsoEnabled ? "Accept Invitation" : "Choose a Password"}
        </Typography>
        {isSsoEnabled ? null :
          <>
            <TextField
              label="Password"
              margin="normal"
              name="password"
              type="password"
              />
            <TextField
              label="Confirm Password"
              margin="normal"
              name="confirmPassword"
              type="password"
            />
            <Box sx={{ alignItems: 'center', display: 'flex', ml: -1, mt: 2 }} >
            </Box>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </>
        }
        <Box sx={{ mt: 2 }}>
          <Button
            color="primary"
            disabled={isSubmitting}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            Submit
          </Button>
        </Box>
        {isSsoEnabled && errors.submit && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>
              {errors.submit}
            </FormHelperText>
          </Box>
        )}
        <TermsOfUseText leadingText="By creating an account, you agree to OnData's" TextProps={{ mt:3 }} />

      </OdForm>
    </Box>
  );
};

export default InvitationConfirmForm;
